/**
 * PhotoUploader Component
 * Camera/file upload UI for facility photos with compression and tagging
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Typography,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import View360Icon from '@mui/icons-material/Vrpano';
import PhotoIcon from '@mui/icons-material/Photo';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { photoService, PhotoUploadData } from '@services/photoService';
import { telegram } from '@services/telegram';
import { requestPreciseLocation } from '@services/highPrecisionLocation';

interface PhotoUploaderProps {
  facilityId: string;
  facilityType?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function PhotoUploader({
  facilityId,
  facilityType = 'well',
  open,
  onClose,
  onSuccess
}: PhotoUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [photoType, setPhotoType] = useState<'standard' | 'panorama_360'>('standard');
  const [caption, setCaption] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const tagOptions = photoService.getTagOptions();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      telegram.showAlert('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      telegram.showAlert('File too large. Maximum size is 10MB');
      return;
    }

    // Compress the image
    setCompressing(true);
    try {
      const compressedFile = await photoService.compressImage(file, 1920, 0.8);
      setSelectedFile(compressedFile);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Compression error:', error);
      // Fall back to original file
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
    setCompressing(false);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(10);
    telegram.hapticFeedback('medium');

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const uploadData: PhotoUploadData = {
        file: selectedFile,
        facilityId,
        facilityType,
        photoType,
        caption: caption.trim() || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined
      };

      // Try to get current location
      try {
        const result = await requestPreciseLocation({
          desiredAccuracyMeters: 20,
          refinementTimeoutMs: 5000,
          allowLastKnownFallback: true,
        });

        if (result.fix) {
          uploadData.latitude = result.fix.lat;
          uploadData.longitude = result.fix.lng;
        } else if (result.reason) {
          console.warn('Photo upload location unavailable:', result.reason);
        }
      } catch (error) {
        console.error('Photo upload precise location error:', error);
      }

      const result = await photoService.uploadPhoto(uploadData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        telegram.hapticFeedback('success');
        telegram.showAlert('📸 Photo uploaded successfully!');
        onSuccess();
        handleClose();
      } else {
        telegram.hapticFeedback('error');
        telegram.showAlert(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      telegram.hapticFeedback('error');
      telegram.showAlert('Failed to upload photo');
    }

    setUploading(false);
    setUploadProgress(0);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setPhotoType('standard');
    setCaption('');
    setSelectedTags([]);
    onClose();
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e293b',
          border: '1px solid rgba(96, 165, 250, 0.2)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(96, 165, 250, 0.2)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CameraAltIcon sx={{ color: '#60a5fa' }} />
          Upload Photo
        </Box>
        <IconButton onClick={handleClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Hidden File Inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* File Selection or Preview */}
        {!preview ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {compressing && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CyberSpinner size={40} />
              </Box>
            )}
            
            <Button
              variant="contained"
              startIcon={<CameraAltIcon />}
              onClick={openCamera}
              disabled={compressing}
              sx={{
                backgroundColor: '#3b82f6',
                color: 'white',
                py: 2,
                '&:hover': { backgroundColor: '#2563eb' }
              }}
            >
              Take Photo
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={openGallery}
              disabled={compressing}
              sx={{
                borderColor: 'rgba(96, 165, 250, 0.5)',
                color: '#60a5fa',
                py: 2,
                '&:hover': { 
                  borderColor: '#60a5fa',
                  backgroundColor: 'rgba(96, 165, 250, 0.1)' 
                }
              }}
            >
              Choose from Gallery
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            {/* Preview Image */}
            <Box sx={{ 
              position: 'relative', 
              borderRadius: 2, 
              overflow: 'hidden',
              mb: 2
            }}>
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 8
                }}
              />
              <IconButton
                onClick={() => { setSelectedFile(null); setPreview(null); }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Photo Type Toggle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                Photo Type
              </Typography>
              <ToggleButtonGroup
                value={photoType}
                exclusive
                onChange={(_, value) => value && setPhotoType(value)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#94a3b8',
                    borderColor: 'rgba(96, 165, 250, 0.3)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(96, 165, 250, 0.2)',
                      color: '#60a5fa',
                      borderColor: '#60a5fa'
                    }
                  }
                }}
              >
                <ToggleButton value="standard">
                  <PhotoIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Standard
                </ToggleButton>
                <ToggleButton value="panorama_360">
                  <View360Icon sx={{ mr: 0.5, fontSize: 18 }} />
                  360° Panorama
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Caption */}
            <TextField
              fullWidth
              label="Caption (optional)"
              placeholder="Describe the photo..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              multiline
              rows={2}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(96, 165, 250, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(96, 165, 250, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
                },
                '& .MuiInputLabel-root': { color: '#94a3b8' },
                '& .MuiInputBase-input::placeholder': { color: '#64748b' }
              }}
            />

            {/* Tags */}
            <Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                Tags (optional) - Help others find this photo
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tagOptions.map(tag => (
                  <Chip
                    key={tag.value}
                    label={`${tag.icon} ${tag.label}`}
                    size="small"
                    onClick={() => handleTagToggle(tag.value)}
                    sx={{
                      backgroundColor: selectedTags.includes(tag.value) 
                        ? 'rgba(96, 165, 250, 0.3)' 
                        : 'rgba(30, 41, 59, 0.8)',
                      color: selectedTags.includes(tag.value) ? '#60a5fa' : '#94a3b8',
                      border: selectedTags.includes(tag.value) 
                        ? '1px solid #60a5fa' 
                        : '1px solid rgba(96, 165, 250, 0.2)',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(96, 165, 250, 0.2)'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress}
              sx={{
                backgroundColor: 'rgba(96, 165, 250, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#22c55e'
                }
              }}
            />
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1, textAlign: 'center' }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(96, 165, 250, 0.2)' }}>
        <Button 
          onClick={handleClose}
          sx={{ color: '#94a3b8' }}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={uploading ? <CyberSpinner size={18} /> : <CheckIcon />}
          sx={{
            backgroundColor: '#22c55e',
            '&:hover': { backgroundColor: '#16a34a' },
            '&.Mui-disabled': { backgroundColor: 'rgba(34, 197, 94, 0.3)' }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
